import { create } from 'zustand';

import { handleEngagement } from '@services/interactions-service';
import { getCommentsDetails, updateEngagement } from '@services/youtube-platform.service';
import { getFeatureComments } from '@components/Features/WinnerWheel/wheel-canvas.service';
import { getUserNameOfCommentor } from '@components/Features/WinnerWheel/helpers/getUserNameOfCommenters';

type AnyFn = (...args: any[]) => any;
type AnyRecord = Record<string, any>;

const truncateText = (text: string) =>
  text?.length > 22 ? `${text.slice(0, 22)}...` : text;

export interface WheelState {
    // --- wheel config ---
    winnerInfo: any;
    winner: string | null;
    wheelId: string | null;
    fsWinnerId: string | undefined;
    wheelState: boolean | string | null;

    // --- wheel options ---
    opts: any[];
    options: any[];
    newWheelUser: any[];

    // --- wheel display ---
    noUser: boolean;
    isExploding: boolean;
    showConfetti: boolean;
    enableWheelComments: boolean;

    // --- external props (synced from WheelProvider) ---
    id: string;
    type: string;
    color: any;
    setting: any;
    metadata: any;
    rosOptions: any[];
    streamId: string;
    hiddenData: AnyRecord;
    platformType: string;
    interactionState: string;
    startInteraction: any;
    currentRosResult: any[];
    currentRosFeature: any;
    poolingInteraction: boolean;
    isRos: boolean;

    // --- external callbacks (synced from WheelProvider) ---
    updateFSMeta: AnyFn;
    setIsStarted: (v: boolean) => void;
    setHiddenData: (data: AnyRecord) => void;
    setStartInteraction: AnyFn;
    setInteractionState: (s: string) => void;
    setResetResultClicked: (v: boolean) => void;
    setForPoolingInteraction: (v: boolean) => void;

    // --- simple setters ---
    setWinner: (winner: any) => void;
    setWinnerInfo: (winnerInfo: any) => void;
    setWheelId: (wheelId: any) => void;
    setWheelState: (wheelState: any) => void;
    setFsWinnerId: (fsWinnerId?: string) => void;
    setIsExploding: (isExploding: boolean) => void;
    setShowConfetti: (showConfetti: boolean) => void;
    setEnableWheelComments: (enableWheelComments: boolean) => void;
    setNoUser: (noUser: boolean) => void;
    setOpts: (opts: any[]) => void;
    setNewWheelUser: (users: any[]) => void;
    setWheelOptions: (options: any[]) => void;

    // --- actions ---
    handleWheel: (args: { interactionId?: any }) => Promise<void>;
    spinWheel: () => void;
    handleSpin: () => void;
    resetWheel: () => void;
    updateWinner: () => Promise<void>;
    removeParticipant: () => void;
    pollForUsers: (isPolling: boolean) => void;
    getCommentsForInteractionWheel: () => void;
    filterHiddenOptions: () => void;

    // --- lifecycle ---
    syncProps: (props: Partial<WheelState>) => void;
    reset: (setting?: any) => void;

    featureSnapshots: Record<string, FeatureSnapshot>;
    saveFeatureSnapshot: (featureId: string) => void;
    restoreFeatureSnapshot: (featureId: string) => boolean;
}

interface FeatureSnapshot {
    winnerInfo: any;
    winner: string | null;
    wheelId: string | null;
    wheelState: boolean | string | null;
    opts: any[];
    options: any[];
}

export const useWheelStore = create<WheelState>()((set, get) => ({
  // --- initial wheel config ---
  winnerInfo: null,
  winner: null,
  wheelId: null,
  fsWinnerId: undefined,
  wheelState: false,

  // --- initial wheel options ---
  opts: [],
  options: [],
  newWheelUser: [],

  // --- initial wheel display ---
  noUser: false,
  isExploding: false,
  showConfetti: false,
  enableWheelComments: false,

  // --- external props defaults ---
  id: '',
  type: '',
  color: null,
  setting: null,
  metadata: null,
  rosOptions: [],
  streamId: '',
  hiddenData: {},
  platformType: '',
  interactionState: '',
  startInteraction: null,
  currentRosResult: [],
  currentRosFeature: null,
  poolingInteraction: false,
  isRos: false,

  // --- external callback stubs (replaced on first syncProps) ---
  updateFSMeta: () => {},
  setIsStarted: () => {},
  setHiddenData: () => {},
  setStartInteraction: () => {},
  setInteractionState: () => {},
  setResetResultClicked: () => {},
  setForPoolingInteraction: () => {},

  // --- simple setters ---
  setWinner: (winner) => set({ winner }),
  setWinnerInfo: (winnerInfo) => set({ winnerInfo }),
  setWheelId: (wheelId) => set({ wheelId }),
  setWheelState: (wheelState) => set({ wheelState }),
  setFsWinnerId: (fsWinnerId) => set({ fsWinnerId }),
  setIsExploding: (isExploding) => set({ isExploding }),
  setShowConfetti: (showConfetti) => set({ showConfetti }),
  setEnableWheelComments: (enableWheelComments) => set({ enableWheelComments }),
  setNoUser: (noUser) => set({ noUser }),
  setOpts: (opts) => set({ opts }),
  setNewWheelUser: (newWheelUser) => set({ newWheelUser }),
  setWheelOptions: (options) => set({ options }),

  // --- actions ---
  handleWheel: async ({ interactionId = null }) => {
    const { currentRosFeature, streamId, platformType, type, color, setting, updateFSMeta } = get();
    if (!currentRosFeature?.id) return;
    const wheelIdMap = JSON.parse(localStorage.getItem('wheelIdMap') || '{}');
    const localWheelId = wheelIdMap[currentRosFeature.id] ?? null;

    if (localWheelId) {
      set({ wheelId: localWheelId });
      updateFSMeta({ activeInteraction: { type, colors: color, settings: setting, id: localWheelId } });
      await handleEngagement({ streamId, platformType, interactionId: localWheelId, currentRosFeature });
      return;
    }

    const response = await handleEngagement({ streamId, platformType, interactionId, currentRosFeature });
    if (response?.status && response?.entity) {
      const map = JSON.parse(localStorage.getItem('wheelIdMap') || '{}');
      map[currentRosFeature.id] = response.entity.engagementId;
      localStorage.setItem('wheelIdMap', JSON.stringify(map));
      set({ wheelId: response.entity.engagementId });
      updateFSMeta({ activeInteraction: { type, colors: color, settings: setting, id: response.entity.engagementId } });
    }
  },

  spinWheel: () => set({ winner: null, winnerInfo: null, wheelState: 'spin', isExploding: false }),

  handleSpin: () => {
    const { type, setting, setInteractionState } = get();
    const isNewWheel = type === 'newWheel' && setting?.wheelSelectionType !== 'customNames';
    const isKeywordEnabled = setting?.enableKeyword;

    if ((!isKeywordEnabled && isNewWheel) || type === 'wheel') {
      get().handleWheel({ interactionId: null });
    }
    set({ winner: null, winnerInfo: null, wheelState: 'spin', isExploding: false });
    setInteractionState('stopped');
  },

  resetWheel: () => {
    const { currentRosFeature, setResetResultClicked, featureSnapshots } = get();
    const map = JSON.parse(localStorage.getItem('wheelIdMap') || '{}');
    delete map[currentRosFeature.id];
    localStorage.setItem('wheelIdMap', JSON.stringify(map));
    const nextSnapshots = { ...featureSnapshots };
    delete nextSnapshots[currentRosFeature.id];
    set({ winnerInfo: null, wheelId: null, opts: [], options: [], featureSnapshots: nextSnapshots });
    get().handleWheel({ interactionId: null });
    get().getCommentsForInteractionWheel();
    setResetResultClicked(false);
  },

  updateWinner: async () => {
    const { wheelId, winner, type, streamId, setting, poolingInteraction } = get();
    if (!wheelId || !winner) return;

    const isNewWheel = type === 'newWheel' && setting?.wheelSelectionType !== 'customNames';
    const isKeywordEnabled = setting?.enableKeyword;

    const response = await updateEngagement(wheelId, type, {
      winner: winner || null, streamId, type: 'winner',
    });

    if (!response.status) return;
    if ((isNewWheel && isKeywordEnabled) || poolingInteraction) return;
  },

  removeParticipant: () => {
    const { currentRosFeature, setting, hiddenData, id, winnerInfo, setIsStarted, setHiddenData } = get();
    const isKeywordEnabled = setting?.enableKeyword;

    const map = JSON.parse(localStorage.getItem('wheelIdMap') || '{}');
    delete map[currentRosFeature.id];
    localStorage.setItem('wheelIdMap', JSON.stringify(map));

    if (!isKeywordEnabled) get().handleWheel({ interactionId: null });

    setIsStarted(true);
    const currentHiddenIds = hiddenData[id] || [];
    currentHiddenIds.push(winnerInfo.id);
    setHiddenData({ ...hiddenData, [id]: currentHiddenIds });
    set({ winnerInfo: null, wheelState: false, winner: null, isExploding: false });
  },

  pollForUsers: (isPolling) => {
    const {
      isRos, wheelId, streamId, id, hiddenData, wheelState,
      setting, setForPoolingInteraction, setInteractionState,
    } = get();

    getUserNameOfCommentor({
      isRos,
      wheelId,
      streamId,
      rosId: id,
      hiddenData,
      wheelState,
      isPolling,
      keyword: setting?.wheelKeyword,
      showUser: setting?.wheelShowUser,
      setForPoolingInteraction,
      isKeywordEnabled: setting?.enableKeyword,
      turncateWinningWheelOpt: truncateText,
      getCommentsDetails,
      setInteractionState,
      setOpts: (opts: any[]) => set({ opts }),
      setNoUser: (noUser: boolean) => set({ noUser }),
      setWheelState: (wheelState: any) => set({ wheelState }),
      setNewWheelUser: (newWheelUser: any[]) => set({ newWheelUser }),
    });
  },

  getCommentsForInteractionWheel: () => {
    const { setting, id, hiddenData, currentRosFeature, streamId, setInteractionState } = get();
    const hiddenIds = hiddenData[id] || [];

    if (setting?.wheelSelectionType === 'customNames') {
      const manualOptions = currentRosFeature?.options
        ?.filter((opt: any) => !hiddenIds.includes(opt.id))
        ?.map((opt: any, index: number) => ({
          id: index.toString(),
          body: opt.title || opt.body || opt.name,
        })) || [];
      set({ opts: [], options: manualOptions });
    } else if (setting?.wheelInteractionId) {
      getFeatureComments({
        streamId,
        hiddenData,
        question: currentRosFeature,
        wheelInteractionId: setting.wheelInteractionId,
        turncateWinningWheelOpt: truncateText,
        setInteractionState,
        rosId: id,
        setOpts: (opts: any[]) => {
          set({ opts });
          get().filterHiddenOptions();
        },
        setNoUser: (noUser: boolean) => set({ noUser }),
        setNewWheelUser: (newWheelUser: any[]) => set({ newWheelUser }),
      });
    } else {
      get().pollForUsers(false);
    }
  },

  filterHiddenOptions: () => {
    const { id, hiddenData, opts, type, setting, currentRosFeature } = get();
    const hiddenIds = hiddenData[id] || [];
    const isNewWheel = type === 'newWheel' && setting?.wheelSelectionType !== 'customNames';
    const featureOptions = currentRosFeature?.options || [];

    if (isNewWheel) {
      set({ options: [], opts: opts?.filter((opt: any) => !hiddenIds.includes(opt.id)) });
    } else if (!isNewWheel && featureOptions.length > 0) {
      set({ opts: [], options: featureOptions.filter((opt: any) => !hiddenIds.includes(opt.id)) });
    }
  },

  syncProps: (props) => set(props as any),

  featureSnapshots: {},

  saveFeatureSnapshot: (featureId) => {
    if (!featureId) return;
    const { winnerInfo, winner, wheelId, wheelState, opts, options, featureSnapshots } = get();
    const isMidSpin = wheelState === 'spin';
    const snapshot = isMidSpin
      ? { winnerInfo: null, winner: null, wheelId, wheelState: false, opts, options }
      : { winnerInfo, winner, wheelId, wheelState, opts, options };
    set({
      featureSnapshots: {
        ...featureSnapshots,
        [featureId]: snapshot,
      },
    });
  },

  restoreFeatureSnapshot: (featureId) => {
    if (!featureId) return false;
    const snap = get().featureSnapshots[featureId];
    if (!snap) return false;
    set(snap as any);
    return true;
  },

  reset: (setting?: any) => set({
    winnerInfo: null,
    winner: null,
    wheelId: null,
    fsWinnerId: undefined,
    wheelState: false,
    opts: [],
    options: [],
    newWheelUser: [],
    noUser: false,
    isExploding: false,
    showConfetti: setting?.enableConfetti ?? false,
    enableWheelComments: setting?.enableWheelComments ?? false,
  }),
}));
